import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PolicyDocButtons } from "@/components/PolicyDocButtons";

const Box = ({ children, testId }) => (
  <div className="grid-lines flex min-h-[calc(100vh-4rem)] items-center justify-center px-4">
    <div className="fade-up w-full max-w-md rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid={testId}>{children}</div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ phase: "paid", policyId: data.policy_id, policyNumber: data.policy_number, sr22: data.sr22_required !== false });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: "failed" });
      } catch (e) {
        if (e.response?.status === 404) return setState({ phase: "failed" });
      }
      tries += 1;
      if (tries >= 15) return setState({ phase: "timeout" });
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state.phase === "checking") return <Box testId="payment-checking"><Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-700" /><p className="mt-4 font-semibold">Confirming your payment…</p></Box>;
  if (state.phase === "paid") {
    return (
      <Box testId="payment-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h1 className="mt-4 text-2xl font-extrabold">Payment confirmed</h1>
        <p className="mt-2 text-slate-600">{state.sr22 ? "Thank you! Your payment is recorded. If this was a new policy, we're preparing your SR22 filing with the state now." : "Thank you! Your payment is recorded and your policy is active. Download or print it below."}</p>
        <Button asChild className="mt-8 bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white" data-testid="view-policy-btn">
          <Link to={state.policyId ? `/policies/${state.policyId}` : "/dashboard"}>{state.sr22 ? "Track my filing" : "View my policy"}</Link>
        </Button>
        {state.policyId && (
          <div className="mt-4 flex justify-center" data-testid="success-policy-docs">
            <PolicyDocButtons policy={{ id: state.policyId, policy_number: state.policyNumber || "document" }} size="sm" />
          </div>
        )}
      </Box>
    );
  }
  return (
    <Box testId="payment-failed">
      <XCircle className="mx-auto h-12 w-12 text-red-500" />
      <h1 className="mt-4 text-2xl font-extrabold">{state.phase === "timeout" ? "Still processing" : "Payment not completed"}</h1>
      <p className="mt-2 text-slate-600">{state.phase === "timeout" ? "Check your account in a minute — it will update automatically." : "No charge was made. You can try again from your account."}</p>
      <Button asChild variant="outline" className="mt-8" data-testid="back-dashboard-btn"><Link to="/dashboard">Go to my account</Link></Button>
    </Box>
  );
};

export const PaymentCancel = () => (
  <Box testId="payment-cancel">
    <XCircle className="mx-auto h-12 w-12 text-slate-400" />
    <h1 className="mt-4 text-2xl font-extrabold">Checkout cancelled</h1>
    <p className="mt-2 text-slate-600">No charge was made. Your quote is saved in your account.</p>
    <Button asChild className="mt-8 bg-blue-700 text-white hover:bg-blue-800 hover:text-white" data-testid="cancel-dashboard-btn"><Link to="/dashboard">Back to my account</Link></Button>
  </Box>
);
